import { StyleSheet, css } from 'aphrodite';
import * as React from 'react';
import { useFormContext } from 'react-hook-form';
import { toast } from 'sonner';
import { IDonationSurvey } from '../../../../../extViewmodels';
import { DonationSurveyHeader } from '../../../../../surveys/components/surveys/DonationSurvey/DonationSurveyHeader';
import { DonationFrequency } from '../../../../../surveys/models/enums/DonationFrequency';
import { computeVisibleFields } from '../../../../../surveys/containers/SurveyResponses/shared/hooks/useConditionalVisibility';
import { Button } from '../../../../components-v2/Button';
import { Input } from '../../../../components-v2/Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../components-v2/Select';
import { THEME } from '../../../../components-v2/Theme/theme';
import { Checkbox } from '../../../../components/Checkbox';
import { Separator } from '../../../../components/Separator';
import { baseStyleSheet, CssSelectors } from '../../../../styles/styles';
import { DonationSurveyFormData } from '../utils';

interface IProps {
	survey: IDonationSurvey;
}

// Aphrodite stylesheet matching live DonationForm styles
const previewStyleSheet = StyleSheet.create({
	container: {
		display: 'flex',
		boxSizing: 'border-box',
		flexDirection: 'column',
		padding: 16,
		gap: 20,
		maxWidth: 968,
		margin: '0 auto',

		'@media (min-width: 722px)': {
			marginTop: THEME.spacing.xxl,
			padding: 0,
		},
	},
	section: {
		display: 'flex',
		flexDirection: 'column',
		gap: 20,
		padding: 20,
	},
	valuePicker: {
		display: 'flex',
		flexDirection: 'column',
		gap: 16,
		padding: 20,
	},
	subtitle: {
		fontSize: THEME.typography.fontSizes.md,
		fontWeight: THEME.typography.fontWeights.bold,
		marginBottom: THEME.spacing.md,
		color: THEME.colors.text.main,
	},
	subtitleCentered: {
		textAlign: 'center',
	},
	frequencyContainer: {
		display: 'flex',
		flexDirection: 'column',
		gap: 8,
	},
	frequenciesGrid: {
		display: 'grid',
		gap: 12,
	},
	frequencyButton: {
		width: '100%',
	},
	amountsContainer: {
		display: 'grid',
		gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
		gap: 12,
	},
	inputContainer: {
		display: 'flex',
		flexGrow: 1,
		flexDirection: 'column',
		alignItems: 'flex-start',
		justifyContent: 'flex-start',
		gap: 8,
	},
	customerInfoContainer: {
		display: 'grid',
		gridTemplateColumns: '1fr',
		gap: 16,
		alignItems: 'flex-start',
		justifyContent: 'flex-start',

		'@media (min-width: 722px)': {
			gridTemplateColumns: 'repeat(3, 1fr)',
		},
	},
	label: {
		fontSize: 14,
		fontWeight: 600,
		color: THEME.colors.text.main,
	},
	buttonContainer: {
		padding: 20,
		width: '100%',
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
	},
	buttonSubmit: {
		width: '100%',
		maxWidth: 350,
	},
	previewNote: {
		marginTop: -8,
		paddingBottom: 20,
		paddingLeft: 20,
		paddingRight: 20,
		fontSize: 12,
		color: THEME.colors.text.light,
	},
	surveyNameHeader: {
		textAlign: 'center',
		marginBottom: THEME.spacing.xl,
		paddingBottom: THEME.spacing.lg,
	},
	surveyName: {
		fontSize: 28,
		fontWeight: 600,
		color: THEME.colors.text.main,
		margin: 0,
	},
	required: {
		color: THEME.colors.danger.main,
		fontWeight: 'bold',
	},
	checkboxContainer: {
		display: 'flex',
		flexDirection: 'column',
		gap: 8,
	},
	checkbox: {
		...CssSelectors.allChildren(
			{
				gap: 8,
			},
			'.checkbox-content'
		),
	},
	checkboxLabel: {
		marginLeft: 8,
	},
	singleFrequencyText: {
		fontSize: THEME.typography.fontSizes.xl,
		fontWeight: THEME.typography.fontWeights.standard,
		textAlign: 'center',
		margin: 0,
	},
	// Checkout preview styles
	checkoutContainer: {
		maxWidth: 600,
		margin: '0 auto',
		padding: 40,
	},
	checkoutHeader: {
		marginBottom: 24,
		paddingBottom: 16,
		borderBottom: `1px solid ${THEME.colors.border.main}`,
	},
	checkoutTitle: {
		fontSize: 24,
		fontWeight: 600,
		color: THEME.colors.text.main,
		marginBottom: 8,
	},
	checkoutNote: {
		fontSize: 14,
		color: THEME.colors.text.light,
	},
	summaryBox: {
		marginBottom: 24,
		padding: 16,
		backgroundColor: THEME.colors.background.light,
		borderRadius: THEME.common.borderRadius,
	},
	summaryRow: {
		display: 'flex',
		justifyContent: 'space-between',
		marginBottom: 8,
	},
	summaryTotal: {
		display: 'flex',
		justifyContent: 'space-between',
		paddingTop: 8,
		borderTop: `1px solid ${THEME.colors.border.main}`,
		fontWeight: 600,
		fontSize: 18,
	},
	stripePlaceholder: {
		padding: 40,
		border: `2px dashed ${THEME.colors.border.main}`,
		borderRadius: THEME.common.borderRadius,
		textAlign: 'center',
		backgroundColor: THEME.colors.background.main,
		marginBottom: 24,
	},
	checkoutButtons: {
		display: 'flex',
		gap: 12,
		justifyContent: 'space-between',
	},
	thankYouContainer: {
		maxWidth: 600,
		margin: '0 auto',
		padding: 40,
		textAlign: 'center',
	},
	thankYouIcon: {
		fontSize: 64,
		marginBottom: 16,
		color: THEME.colors.success.main,
	},
	thankYouTitle: {
		fontSize: 24,
		fontWeight: 600,
		marginBottom: 16,
		color: THEME.colors.success.main,
	},
	thankYouMessage: {
		fontSize: 16,
		color: THEME.colors.text.main,
		marginBottom: 24,
	},
});

/**
 * Preview component for Donation Survey
 * Displays a real-time preview of how the donation survey will appear to donors
 * Updates automatically as the user modifies settings in the Settings tabs
 */
export const PreviewDonationSurveyForm: React.FC<IProps> = ({ survey: baseSurvey }) => {
	const formContext = useFormContext<DonationSurveyFormData>();
	const parentFormValues = formContext?.watch();

	// Merge base survey data with live form values for real-time preview
	const survey = React.useMemo<IDonationSurvey>(() => {
		const mergedSurvey: IDonationSurvey = {
			...baseSurvey,
			donationOptions: {
				...baseSurvey.donationOptions,
			},
		};

		// Update survey name from live form values
		if (parentFormValues?.name) {
			mergedSurvey.name = parentFormValues.name;
		}

		// Update donation options from live form values
		if (parentFormValues) {
			mergedSurvey.donationOptions = {
				...mergedSurvey.donationOptions,
				title: parentFormValues.donationPageTitle || '',
				donationDescription: parentFormValues.causeDetails || '',
				image: parentFormValues.image || null,
				enableSuggestedAmounts: parentFormValues.hasDonationValues ?? false,
				suggestedAmounts: parentFormValues.donationValues?.map(v => v.value) || [],
				allowCustomAmount: parentFormValues.allowCustomDonationAmount ?? false,
				requireMinimumAmount: parentFormValues.hasMinimumDonationAmount ?? false,
				minimumAmount: parentFormValues.minimumDonationAmount || 0,
				collectDonorPhone: {
					collect: parentFormValues.collectDonorPhone === 'collect',
					require: parentFormValues.collectDonorPhone === 'require',
					updateExisting: false,
				},
				collectDonorAddress: {
					collect: parentFormValues.collectDonorAddress === 'collect',
					require: parentFormValues.collectDonorAddress === 'require',
					updateExisting: false,
				},
				collectDonorName: { collect: false, require: true, updateExisting: false },
				collectDonorEmail: { collect: false, require: true, updateExisting: false },
				sendConfirmationEmail: true,
				// Add recurring donation settings
				// Compute allowRecurringDonations from individual frequency settings
				allowRecurringDonations:
					(parentFormValues.monthlySettings?.enabled || parentFormValues.annualSettings?.enabled) ??
					mergedSurvey.donationOptions.allowRecurringDonations ??
					false,
				monthlyDonationSettings: parentFormValues.monthlySettings
					? {
							enabled: parentFormValues.monthlySettings.enabled ?? false,
							enableSuggestedAmounts: parentFormValues.monthlySettings.hasDonationValues ?? false,
							suggestedAmounts: parentFormValues.monthlySettings.donationValues?.map(v => v.value) || [],
							allowCustomAmount: parentFormValues.monthlySettings.allowCustomDonationAmount ?? false,
							requireMinimumAmount: false,
							minimumAmount: 0,
						}
					: mergedSurvey.donationOptions.monthlyDonationSettings,
				annualDonationSettings: parentFormValues.annualSettings
					? {
							enabled: parentFormValues.annualSettings.enabled ?? false,
							enableSuggestedAmounts: parentFormValues.annualSettings.hasDonationValues ?? false,
							suggestedAmounts: parentFormValues.annualSettings.donationValues?.map(v => v.value) || [],
							allowCustomAmount: parentFormValues.annualSettings.allowCustomDonationAmount ?? false,
							requireMinimumAmount: false,
							minimumAmount: 0,
						}
					: mergedSurvey.donationOptions.annualDonationSettings,
				oneTimeDonationSettings: {
					// Use enabled value from form, default to true for backward compatibility
					enabled: parentFormValues.oneTimeSettings?.enabled ?? true,
					enableSuggestedAmounts:
						parentFormValues.oneTimeSettings?.hasDonationValues || parentFormValues.hasDonationValues || false,
					suggestedAmounts: (() => {
						const oneTimeValues = parentFormValues.oneTimeSettings?.donationValues?.map(v => v.value) || [];
						const legacyValues = parentFormValues.donationValues?.map(v => v.value) || [];
						return oneTimeValues.length > 0 ? oneTimeValues : legacyValues;
					})(),
					allowCustomAmount:
						parentFormValues.oneTimeSettings?.allowCustomDonationAmount ||
						parentFormValues.allowCustomDonationAmount ||
						false,
					requireMinimumAmount:
						parentFormValues.oneTimeSettings?.hasMinimumDonationAmount ||
						parentFormValues.hasMinimumDonationAmount ||
						false,
					minimumAmount:
						parentFormValues.oneTimeSettings?.minimumDonationAmount || parentFormValues.minimumDonationAmount || 0,
				},
			};

			// Update service fee settings
			if (parentFormValues.allowCustomerToCoverServiceFees !== undefined) {
				mergedSurvey.serviceFeeSettings = {
					...mergedSurvey.serviceFeeSettings,
					enabled: parentFormValues.allowCustomerToCoverServiceFees,
					allowCustomerToCoverServiceFees: parentFormValues.allowCustomerToCoverServiceFees,
					calculationMethod: parentFormValues.serviceFeeCalculationMethod,
					fixedFeeAmount: parentFormValues.serviceFeeFixedAmount,
					percentageFee: parentFormValues.serviceFeePercentage,
				};
			}

			// Update available campaigns
			if (parentFormValues.programSpecificDonation) {
				mergedSurvey.availableCampaigns = parentFormValues.programSpecificDonation;
				mergedSurvey.availableCampaignIds = parentFormValues.programSpecificDonation.map(c => c.id);
			}

			// Sync requireCampaignSelection from live form values
			if (parentFormValues.requireCampaignSelection !== undefined) {
				mergedSurvey.donationOptions = {
					...mergedSurvey.donationOptions,
					requireCampaignSelection: parentFormValues.requireCampaignSelection,
				};
			}

			// Update custom form
			if (parentFormValues.customForm) {
				mergedSurvey.customForm = parentFormValues.customForm;
			}
		}

		return mergedSurvey;
	}, [baseSurvey, parentFormValues]);

	// Check if one-time donations are enabled
	const oneTimeEnabled = survey.donationOptions?.oneTimeDonationSettings?.enabled ?? true;

	// Build list of enabled recurring frequencies (excluding one-time)
	const enabledFrequencies = React.useMemo(() => {
		const frequencies: DonationFrequency[] = [];
		if (survey.donationOptions?.monthlyDonationSettings?.enabled) {
			frequencies.push(DonationFrequency.Monthly);
		}
		if (survey.donationOptions?.annualDonationSettings?.enabled) {
			frequencies.push(DonationFrequency.Yearly);
		}
		return frequencies;
	}, [survey.donationOptions]);

	// State for selected frequency in preview
	const [selectedFrequency, setSelectedFrequency] = React.useState<DonationFrequency | null>(null);

	// Track Dropdown/Checkbox selections so conditional-visibility rules can react
	// to them in the preview (mirrors PreviewCustomSurveyForm).
	const [previewSelections, setPreviewSelections] = React.useState<Record<string, string>>({});

	// Pull surveyForm from live form values first (so edits in the Questions tab
	// reflect immediately), falling back to the saved survey.
	const liveSurveyForm = parentFormValues?.surveyForm ?? baseSurvey?.surveyForm;
	const visibleFieldIds = React.useMemo(() => {
		if (!liveSurveyForm?.surveyParts?.length) return undefined;
		return computeVisibleFields(liveSurveyForm, previewSelections);
	}, [liveSurveyForm, previewSelections]);

	// Set of fieldIds represented in surveyForm.surveyParts. Fields not listed
	// here aren't governed by conditional visibility and should default to visible.
	// This guards against stale or partial surveyForm data hiding fields silently.
	const surveyPartFieldIds = React.useMemo(() => {
		if (!liveSurveyForm?.surveyParts?.length) return undefined;
		return new Set(
			liveSurveyForm.surveyParts.map(part => (part as { fieldId?: string }).fieldId).filter((id): id is string => !!id)
		);
	}, [liveSurveyForm]);

	const isCustomFieldVisible = (fieldId?: string) => {
		if (!visibleFieldIds || !surveyPartFieldIds) return true;
		if (!fieldId) return true;
		if (!surveyPartFieldIds.has(fieldId)) return true;
		return visibleFieldIds.has(fieldId);
	};

	const handleDropdownChange = (fieldId: string, value: string) => {
		setPreviewSelections(prev => ({ ...prev, [fieldId]: value }));
	};

	const handleCheckboxChange = (fieldId: string, optionId: string, checked: boolean) => {
		setPreviewSelections(prev => {
			const current = prev[fieldId] ?? '';
			const selected = current.split(',').filter(Boolean);
			const updated = checked ? [...selected, optionId] : selected.filter(id => id !== optionId);
			return { ...prev, [fieldId]: updated.join(',') };
		});
	};

	// Auto-select first recurring frequency if one-time is disabled and nothing is selected
	React.useEffect(() => {
		if (!oneTimeEnabled && selectedFrequency === null && enabledFrequencies.length > 0) {
			setSelectedFrequency(enabledFrequencies[0]);
		}
	}, [oneTimeEnabled, selectedFrequency, enabledFrequencies]);

	// Calculate total number of frequency options (including one-time if enabled)
	const totalFrequencyOptions = (oneTimeEnabled ? 1 : 0) + enabledFrequencies.length;

	// Get current frequency settings based on selection
	const currentFrequencySettings = React.useMemo(() => {
		const options = survey.donationOptions;
		if (!selectedFrequency) {
			return options.oneTimeDonationSettings || options;
		}
		if (selectedFrequency === DonationFrequency.Monthly) {
			return options.monthlyDonationSettings || options.oneTimeDonationSettings || options;
		}
		if (selectedFrequency === DonationFrequency.Yearly) {
			return options.annualDonationSettings || options.oneTimeDonationSettings || options;
		}
		return options.oneTimeDonationSettings || options;
	}, [survey.donationOptions, selectedFrequency]);

	const hasSuggestedAmounts =
		currentFrequencySettings?.enableSuggestedAmounts && currentFrequencySettings?.suggestedAmounts?.length > 0;
	const hasCampaigns = survey.availableCampaigns && survey.availableCampaigns.length > 0;

	const handlePreviewSubmit = React.useCallback(() => {
		toast.info('Preview only: Opening checkout form...');
	}, []);

	// Show main donation form
	return (
		<div className={css(previewStyleSheet.container)}>
			{survey.name && (
				<div className={css(previewStyleSheet.surveyNameHeader)}>
					<h1 className={css(previewStyleSheet.surveyName)}>{survey.name}</h1>
				</div>
			)}
			<DonationSurveyHeader
				description={survey.donationOptions?.donationDescription || ''}
				image={survey.donationOptions?.image?.url}
			/>

			<Separator />

			{/* Donation Amount Section */}
			<div className={css(previewStyleSheet.valuePicker)}>
				{/* Frequency Selector - show if there are any frequency options */}
				{totalFrequencyOptions > 0 && (
					<>
						{/* If only one frequency option, show text instead of buttons */}
						{totalFrequencyOptions === 1 ? (
							<section className={css(previewStyleSheet.frequencyContainer)}>
								<h3 className={css(previewStyleSheet.singleFrequencyText)}>
									{oneTimeEnabled && enabledFrequencies.length === 0
										? 'This is a one-time donation.'
										: enabledFrequencies.includes(DonationFrequency.Monthly)
											? 'This is a monthly recurring donation.'
											: 'This is a yearly recurring donation.'}
								</h3>
							</section>
						) : (
							/* Show buttons when multiple frequency options are available */
							<div className={css(previewStyleSheet.frequencyContainer)}>
								<h3 className={css(previewStyleSheet.subtitle, previewStyleSheet.subtitleCentered)}>
									Choose donation frequency
								</h3>
								<div
									className={css(previewStyleSheet.frequenciesGrid)}
									style={{ gridTemplateColumns: `repeat(${totalFrequencyOptions}, 1fr)` }}
								>
									{/* One-time donation button - only show if enabled */}
									{oneTimeEnabled && (
										<Button
											variant={selectedFrequency === null ? 'primary' : 'secondary'}
											styles={[previewStyleSheet.frequencyButton]}
											type='button'
											onClick={() => setSelectedFrequency(null)}
										>
											One time
										</Button>
									)}
									{enabledFrequencies.includes(DonationFrequency.Monthly) && (
										<Button
											variant={selectedFrequency === DonationFrequency.Monthly ? 'primary' : 'secondary'}
											styles={[previewStyleSheet.frequencyButton]}
											type='button'
											onClick={() => setSelectedFrequency(DonationFrequency.Monthly)}
										>
											Monthly
										</Button>
									)}
									{enabledFrequencies.includes(DonationFrequency.Yearly) && (
										<Button
											variant={selectedFrequency === DonationFrequency.Yearly ? 'primary' : 'secondary'}
											styles={[previewStyleSheet.frequencyButton]}
											type='button'
											onClick={() => setSelectedFrequency(DonationFrequency.Yearly)}
										>
											Yearly
										</Button>
									)}
								</div>
							</div>
						)}
					</>
				)}

				<p className={css(previewStyleSheet.subtitle)}>Select your donation amount</p>

				{/* Suggested Amounts */}
				{hasSuggestedAmounts && (
					<div className={css(previewStyleSheet.amountsContainer)}>
						{currentFrequencySettings?.suggestedAmounts?.map((amount, index) => (
							<Button key={index} variant='secondary' type='button' disabled>
								$ {amount}
							</Button>
						))}
					</div>
				)}

				{/* Custom Amount */}
				{currentFrequencySettings?.allowCustomAmount && (
					<div className={css(previewStyleSheet.inputContainer)}>
						<label htmlFor='preview-custom-amount' className={css(previewStyleSheet.label)}>
							{hasSuggestedAmounts ? '$ Other amount' : 'Donation amount'}
							{currentFrequencySettings.requireMinimumAmount && ` (minimum $${currentFrequencySettings.minimumAmount})`}
						</label>
						<Input id='preview-custom-amount' type='text' disabled placeholder='PREVIEW ONLY' />
					</div>
				)}

				{/* Cover Fees Option */}
				{survey.serviceFeeSettings?.allowCustomerToCoverServiceFees && (
					<Checkbox checked={false} disabled>
						Cover transaction fees (Preview only)
					</Checkbox>
				)}
			</div>

			{/* Specific Cause Selector */}
			{hasCampaigns && (
				<>
					<Separator />
					<div className={css(previewStyleSheet.section)}>
						<div className={css(previewStyleSheet.inputContainer)}>
							<label htmlFor='preview-campaign' className={css(previewStyleSheet.label)}>
								Select a Cause{' '}
								{survey.donationOptions?.requireCampaignSelection ? (
									<span className={css(previewStyleSheet.required)}>*</span>
								) : (
									'(Optional)'
								)}
							</label>
							<select
								id='preview-campaign'
								disabled
								style={{
									width: '100%',
									padding: 12,
									border: THEME.common.border,
									borderRadius: THEME.common.borderRadius,
									fontSize: 14,
									backgroundColor: THEME.colors.background.main,
								}}
							>
								<option>PREVIEW ONLY</option>
								{survey.availableCampaigns.map(campaign => (
									<option key={campaign.id} value={campaign.id}>
										{campaign.name}
									</option>
								))}
							</select>
						</div>
					</div>
				</>
			)}

			<Separator />

			{/* Donor Information */}
			<div className={css(previewStyleSheet.section)}>
				<div className={css(previewStyleSheet.customerInfoContainer)}>
					<div className={css(previewStyleSheet.inputContainer)}>
						<label htmlFor='preview-first-name' className={css(previewStyleSheet.label)}>
							First Name <span className={css(previewStyleSheet.required)}>*</span>
						</label>
						<Input id='preview-first-name' type='text' disabled placeholder='PREVIEW ONLY' />
					</div>
					<div className={css(previewStyleSheet.inputContainer)}>
						<label htmlFor='preview-last-name' className={css(previewStyleSheet.label)}>
							Last Name <span className={css(previewStyleSheet.required)}>*</span>
						</label>
						<Input id='preview-last-name' type='text' disabled placeholder='PREVIEW ONLY' />
					</div>
					<div className={css(previewStyleSheet.inputContainer)}>
						<label htmlFor='preview-email' className={css(previewStyleSheet.label)}>
							Email <span className={css(previewStyleSheet.required)}>*</span>
						</label>
						<Input id='preview-email' type='email' disabled placeholder='PREVIEW ONLY' />
					</div>

					{/* Phone - conditional */}
					{(survey.donationOptions?.collectDonorPhone?.collect ||
						survey.donationOptions?.collectDonorPhone?.require) && (
						<div className={css(previewStyleSheet.inputContainer)}>
							<label htmlFor='preview-phone' className={css(previewStyleSheet.label)}>
								Phone{' '}
								{survey.donationOptions?.collectDonorPhone?.require && (
									<span className={css(previewStyleSheet.required)}>*</span>
								)}
							</label>
							<Input id='preview-phone' type='tel' disabled placeholder='PREVIEW ONLY' />
						</div>
					)}
				</div>

				{/* Address - conditional */}
				{(survey.donationOptions?.collectDonorAddress?.collect ||
					survey.donationOptions?.collectDonorAddress?.require) && (
					<>
						<div className={css(previewStyleSheet.inputContainer)}>
							<label htmlFor='preview-address1' className={css(previewStyleSheet.label)}>
								Street Address{' '}
								{survey.donationOptions?.collectDonorAddress?.require && (
									<span className={css(previewStyleSheet.required)}>*</span>
								)}
							</label>
							<Input id='preview-address1' type='text' disabled placeholder='PREVIEW ONLY' />
						</div>
						<div className={css(previewStyleSheet.inputContainer)}>
							<label htmlFor='preview-city' className={css(previewStyleSheet.label)}>
								City{' '}
								{survey.donationOptions?.collectDonorAddress?.require && (
									<span className={css(previewStyleSheet.required)}>*</span>
								)}
							</label>
							<Input id='preview-city' type='text' disabled placeholder='PREVIEW ONLY' />
						</div>
						<div className={css(previewStyleSheet.inputContainer)}>
							<label htmlFor='preview-state' className={css(previewStyleSheet.label)}>
								State{' '}
								{survey.donationOptions?.collectDonorAddress?.require && (
									<span className={css(previewStyleSheet.required)}>*</span>
								)}
							</label>
							<Input id='preview-state' type='text' disabled placeholder='PREVIEW ONLY' />
						</div>
						<div className={css(previewStyleSheet.inputContainer)}>
							<label htmlFor='preview-zip' className={css(previewStyleSheet.label)}>
								ZIP Code{' '}
								{survey.donationOptions?.collectDonorAddress?.require && (
									<span className={css(previewStyleSheet.required)}>*</span>
								)}
							</label>
							<Input id='preview-zip' type='text' disabled placeholder='PREVIEW ONLY' />
						</div>
						<div className={css(previewStyleSheet.inputContainer)}>
							<label htmlFor='preview-country' className={css(previewStyleSheet.label)}>
								Country{' '}
								{survey.donationOptions?.collectDonorAddress?.require && (
									<span className={css(previewStyleSheet.required)}>*</span>
								)}
							</label>
							<Input id='preview-country' type='text' disabled placeholder='PREVIEW ONLY' />
						</div>
					</>
				)}
			</div>

			{/* Custom Questions */}
			{survey?.customForm?.fields && survey.customForm.fields.length > 0 && (
				<>
					<Separator />
					<div className={css(previewStyleSheet.section)}>
						{survey.customForm.fields
							.filter(field => !field.isHidden)
							.filter(field => isCustomFieldVisible(field.id))
							.map((field, index) => {
								const fieldId = field.id || `preview-${index}`;
								return (
									<div key={fieldId} className={css(previewStyleSheet.inputContainer)}>
										<label htmlFor={`preview-custom-${fieldId}`} className={css(previewStyleSheet.label)}>
											{field.label}
											{!field.isOptional && <span className={css(previewStyleSheet.required)}> *</span>}
										</label>
										{field.fieldType === 'String' && (
											<Input id={`preview-custom-${fieldId}`} type='text' disabled placeholder='PREVIEW ONLY' />
										)}
										{field.fieldType === 'Option' && field.options && (
											// Interactive in preview so visibility rules can react to selections.
											<Select
												value={previewSelections[fieldId] ?? ''}
												onValueChange={value => handleDropdownChange(fieldId, value)}
											>
												<SelectTrigger id={`preview-custom-${fieldId}`} styles={[baseStyleSheet.wFull]}>
													<SelectValue placeholder='Select an option...' />
												</SelectTrigger>
												<SelectContent>
													{field.options.map((opt: any, i: number) => {
														const optLabel = typeof opt === 'string' ? opt : opt.label;
														const optValue = typeof opt === 'string' ? opt : opt.id;
														return (
															<SelectItem key={`${fieldId}-${optValue}-${i}`} value={optValue}>
																{optLabel}
															</SelectItem>
														);
													})}
												</SelectContent>
											</Select>
										)}
										{field.fieldType === 'MultipleOptions' && field.options && (
											<div className={css(previewStyleSheet.checkboxContainer)}>
												{field.options.map((opt: any, i: number) => {
													const optLabel = typeof opt === 'string' ? opt : opt.label;
													const optValue = typeof opt === 'string' ? opt : opt.id;
													const selectedValues = (previewSelections[fieldId] ?? '').split(',').filter(Boolean);
													return (
														<Checkbox
															key={`${fieldId}-${optValue}-${i}`}
															checked={selectedValues.includes(optValue)}
															onChange={e =>
																handleCheckboxChange(fieldId, optValue, (e.target as HTMLInputElement).checked)
															}
															className={css(previewStyleSheet.checkbox)}
														>
															{optLabel}
														</Checkbox>
													);
												})}
											</div>
										)}
									</div>
								);
							})}
					</div>
				</>
			)}

			<Separator />

			{/* Submit Button */}
			<div className={css(previewStyleSheet.buttonContainer)}>
				<Button type='button' onClick={handlePreviewSubmit} styles={[previewStyleSheet.buttonSubmit]}>
					Save and Continue
				</Button>
			</div>
		</div>
	);
};
